import EntityModel from '@/common/models/entity-model';
import RESOURCES from '@/common/auth/definitions/resources';

export default class SegmentDataModel extends EntityModel {
    static Pages = {
        lists: 'lists',
        customerDetails: 'customer-details',
    };

    static Categories = {
        starred: 'starred',
        customers: 'customers',
        commerce: 'commerce',
        logs: 'logs',
    };

    static Types = {
        customers: 'customers',
        invoices: 'invoices',
        transactions: 'transactions',
        orders: 'subscriptions',
        couponRedemptions: 'coupon-redemptions',
        apiLogs: 'api-logs',
        webhookLogs: 'webhook-logs',
    };

    static TypesAuth = {
        [SegmentDataModel.Types.customers]: RESOURCES.customers,
        [SegmentDataModel.Types.invoices]: RESOURCES.invoices,
        [SegmentDataModel.Types.transactions]: RESOURCES.transactions,
        [SegmentDataModel.Types.orders]: RESOURCES.subscriptions,
        [SegmentDataModel.Types.couponRedemptions]: RESOURCES.coupons,
        [SegmentDataModel.Types.apiLogs]: 'apiTracking',
        [SegmentDataModel.Types.webhookLogs]: 'webhooksTracking',
    };

    static TypesStructure = {
        [SegmentDataModel.Types.customers]: {
            label: 'Customers',
            category: SegmentDataModel.Categories.customers,
        },
        [SegmentDataModel.Types.invoices]: {
            label: 'Invoices',
            category: SegmentDataModel.Categories.commerce,
        },
        [SegmentDataModel.Types.transactions]: {
            label: 'Transactions',
            category: SegmentDataModel.Categories.commerce,
        },
        [SegmentDataModel.Types.orders]: {
            label: 'Orders',
            category: SegmentDataModel.Categories.commerce,
        },
        [SegmentDataModel.Types.couponRedemptions]: {
            label: 'Coupon Redemptions',
            category: SegmentDataModel.Categories.commerce,
        },
        [SegmentDataModel.Types.apiLogs]: {
            label: 'API Logs',
            category: SegmentDataModel.Categories.logs,
        },
        [SegmentDataModel.Types.webhookLogs]: {
            label: 'Webhooks Logs',
            category: SegmentDataModel.Categories.logs,
        },
    };

    static CategoriesStructure = {
        [SegmentDataModel.Categories.starred]: {
            label: 'Starred',
            types: [],
        },
        [SegmentDataModel.Categories.customers]: {
            label: 'Customers',
            types: [
                SegmentDataModel.Types.customers,
            ],
        },
        [SegmentDataModel.Categories.commerce]: {
            label: 'Commerce',
            types: [
                SegmentDataModel.Types.invoices,
                SegmentDataModel.Types.transactions,
                SegmentDataModel.Types.orders,
                SegmentDataModel.Types.couponRedemptions,
            ],
        },
        [SegmentDataModel.Categories.logs]: {
            label: 'Logs',
            types: [
                SegmentDataModel.Types.apiLogs,
                SegmentDataModel.Types.webhookLogs,
            ],
        },
    };

    static PagesStructure = {
        [SegmentDataModel.Pages.lists]: {
            label: 'Lists',
            categories: [
                SegmentDataModel.Categories.customers,
                SegmentDataModel.Categories.commerce,
                SegmentDataModel.Categories.logs,
            ],
            types: [
                SegmentDataModel.Types.customers,
                SegmentDataModel.Types.invoices,
                SegmentDataModel.Types.transactions,
                SegmentDataModel.Types.orders,
                SegmentDataModel.Types.couponRedemptions,
                SegmentDataModel.Types.apiLogs,
                SegmentDataModel.Types.webhookLogs,
            ],
        },
        [SegmentDataModel.Pages.customerDetails]: {
            label: 'Customer Details',
            categories: [
                SegmentDataModel.Categories.commerce,
                SegmentDataModel.Categories.logs,
            ],
            types: [
                SegmentDataModel.Types.invoices,
                SegmentDataModel.Types.transactions,
                SegmentDataModel.Types.orders,
                SegmentDataModel.Types.couponRedemptions,
                SegmentDataModel.Types.apiLogs,
                SegmentDataModel.Types.webhookLogs,
            ],
        },
    };

    static getPageOptions() {
        return Object.entries(SegmentDataModel.PagesStructure).map(
            ([value, {label}]) => ({label, value}),
        );
    }

    static getTypeOptions(page) {
        return SegmentDataModel.PagesStructure[page].types.map(
            (value) => {
                const {label} = SegmentDataModel.TypesStructure[value];
                return {label, value};
            },
        );
    }

    hydrate({
        name = null,
        page = null,
        type = null,
        filters = null,
        columns = null,
        sort = null,
    } = {}) {
        this.name = name;
        this.page = page || Object.keys(SegmentDataModel.PagesStructure)[0];
        this.type = type || SegmentDataModel.PagesStructure[this.page].types[0];
        this.filters = filters;
        this.columns = columns;
        this.sort = sort || '';
    }

    get sortBy() {
        if (this.sort.charAt(0) === '-') {
            const property = this.sort.substring(1);
            return {[property]: 'desc'};
        }
        return {
            [this.sort]: 'asc',
        };
    }

    get hasCreateCustomerAction() {
        return this.type === SegmentDataModel.Types.customers;
    }

    get canDataExport() {
        return ![
            SegmentDataModel.Types.invoices,
            SegmentDataModel.Types.apiLogs,
            SegmentDataModel.Types.webhookLogs,
        ].includes(this.type);
    }

    get category() {
        return Object.keys(SegmentDataModel.CategoriesStructure)
            .find(group => SegmentDataModel.CategoriesStructure[group].types.includes(this.type));
    }

    getPayload() {
        return {
            name: this.name,
            type: this.type,
            page: this.page,
            filters: this.filters,
            columns: this.columns,
            sort: this.sort,
        };
    }
}
